/**
 * セット商品ページ（main-product-set / main-product-builder）の購入導線。
 *
 *   <product-set-buy-now data-variant-id="...">     … 数量を 1 にしてチェックアウトへ遷移
 *   <product-set-add-to-cart data-variant-id="..."> … カートに追加してカート通知を表示（画面遷移なし）
 *
 * どちらも中の <a> / <button> のクリックを拾う。data-variant-id が無い場合は
 * 祖先の section[data-variant-id] にフォールバックする。
 * エラーは alert ではなく product-set-toast スニペットのトーストで通知する。
 */

const TOAST_DURATION = 6000;
const TOAST_ANIMATION_DURATION = 300;

function routeFor(name, fallback) {
  return (window.routes && window.routes[name]) || fallback;
}

function defaultErrorMessage() {
  return (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your cart.';
}

/**
 * Cart API のエラーメッセージを取り出す。
 * errors の形は一定でない（文字列 / 配列 / { バリアントID or フィールド名: [メッセージ] }）ため
 * 全て平坦化して連結し、無ければ description → message の順にフォールバックする。
 * 「このアイテムの最大数がすでにカートに入っています。」等は errors 側にしか入らないので優先して読む。
 */
function cartErrorMessage(data) {
  if (!data) return null;

  const errors = data.errors;
  let messages = [];
  if (typeof errors === 'string') messages = [errors];
  else if (Array.isArray(errors)) messages = errors;
  else if (errors && typeof errors === 'object') messages = Object.values(errors).flat();

  const text = messages.filter(Boolean).join('\n');
  return text || data.description || data.message || null;
}

/**
 * window.routes の URL は /cart/add, /cart/update（.js 無し）なので、
 * Accept: application/json を付けないとカートページの HTML が返ってきて JSON パースに失敗する。
 */
function postJSON(url, body) {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  }).then((response) =>
    response.json().then((data) => {
      // 200 でも errors を含めて返ってくることがあるので、両方をエラー扱いする
      const message = cartErrorMessage(data);
      if (!response.ok) throw new Error(message || response.statusText);
      if (message) throw new Error(message);
      return data;
    })
  );
}

/**
 * @param {string} message - 表示する文言
 * @param {{ variant?: 'success'|'error', cartLink?: boolean }} [options]
 */
function showToast(message, options = {}) {
  const toast = document.querySelector('.product-set-toast');
  if (!toast) return;

  toast.classList.toggle('product-set-toast--error', options.variant === 'error');

  const body = toast.querySelector('.product-set-toast__message');
  if (body) body.textContent = message;

  const link = toast.querySelector('.product-set-toast__link');
  if (link) {
    link.hidden = !options.cartLink;
    if (options.cartLink) {
      link.textContent = (link.dataset.label || '').replace('[count]', toast.dataset.cartCount || '');
    }
  }

  clearTimeout(toast.dismissTimer);
  clearTimeout(toast.hideTimer);
  toast.hidden = false;
  // hidden を外した直後だとトランジションが走らないので次フレームで可視化する
  requestAnimationFrame(() => toast.classList.add('is-visible'));

  toast.dismissTimer = setTimeout(() => hideToast(toast), TOAST_DURATION);
}

function hideToast(toast) {
  if (!toast) return;
  clearTimeout(toast.dismissTimer);
  clearTimeout(toast.hideTimer);
  toast.classList.remove('is-visible');
  // prefers-reduced-motion 下では transitionend が来ないので時間で hidden に戻す
  toast.hideTimer = setTimeout(() => {
    toast.hidden = true;
  }, TOAST_ANIMATION_DURATION);
}

document.addEventListener('click', (event) => {
  const close = event.target.closest('.product-set-toast__close');
  if (close) hideToast(close.closest('.product-set-toast'));
});

/**
 * ヘッダーのカートアイコン。このテーマでは header.liquid でコメントアウトされていて
 * 通常は DOM に無いが、復活させた場合に点数が更新されるよう存在時のみ差し替える。
 */
function cartIconBubble() {
  return document.getElementById('cart-icon-bubble');
}

function updateCartIconBubble(data) {
  const bubble = cartIconBubble();
  const html = data.sections && data.sections['cart-icon-bubble'];
  if (!bubble || !html) return;

  const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
  if (source) bubble.innerHTML = source.innerHTML;
}

/**
 * カート点数を /cart.js から取り直す。
 *
 * /cart/add.js のレスポンスの quantity は「追加した数量」ではなく
 * 「そのラインの合計数量」（既存カートにある場合は合算値）なので差分を取れない。
 * 数量セレクタで 2 以上を追加した場合にも正しい点数を出すため、
 * 加算ではなくカート全体を読み直す。
 *
 * 失敗しても通知自体は出したいので、エラーは握りつぶす。
 */
function refreshCartCount() {
  const toast = document.querySelector('.product-set-toast');
  if (!toast) return Promise.resolve();

  return fetch(`${routeFor('cart_url', '/cart')}.js`, {
    headers: { Accept: 'application/json' },
  })
    .then((response) => response.json())
    .then((cart) => {
      toast.dataset.cartCount = cart.item_count;
      // すでにトーストが出ている場合は「カートを見る」の件数も追随させる
      const link = toast.querySelector('.product-set-toast__link');
      if (link && !link.hidden) {
        link.textContent = (link.dataset.label || '').replace('[count]', cart.item_count);
      }
    })
    .catch((error) => {
      console.error('product-set-cart: カート点数の取得に失敗しました', error);
    });
}

function addedToCartMessage() {
  const toast = document.querySelector('.product-set-toast');
  return (toast && toast.dataset.addedMessage) || 'Item added to your cart';
}

/** request() と onSuccess() をサブクラスで実装する共通クラス */
class ProductSetCartAction extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.trigger = this.querySelector('a, button');
    if (!this.trigger) return;

    this.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      this.submit();
    });
  }

  get variantId() {
    const section = this.closest('section[data-variant-id]');
    const raw = this.dataset.variantId || (section && section.dataset.variantId);
    const id = parseInt(raw, 10);
    return Number.isNaN(id) ? null : id;
  }

  get busy() {
    return this.trigger.getAttribute('aria-disabled') === 'true';
  }

  set busy(state) {
    if (state) this.trigger.setAttribute('aria-disabled', 'true');
    else this.trigger.removeAttribute('aria-disabled');
  }

  submit() {
    if (this.busy) return;

    const variantId = this.variantId;
    if (!variantId) {
      console.error('product-set-cart: variant_id が取得できませんでした', this);
      showToast(defaultErrorMessage(), { variant: 'error' });
      return;
    }

    this.busy = true;
    this.request(variantId)
      .then((data) => this.onSuccess(data))
      .catch((error) => {
        console.error('product-set-cart:', error);
        showToast(error.message || defaultErrorMessage(), { variant: 'error' });
      })
      .finally(() => {
        this.busy = false;
      });
  }
}

/** 既存カートを保持したまま対象バリアントの数量を 1 にしてチェックアウトへ */
class ProductSetBuyNow extends ProductSetCartAction {
  request(variantId) {
    const updates = {};
    updates[variantId] = 1;
    return postJSON(routeFor('cart_update_url', '/cart/update.js'), { updates });
  }

  onSuccess() {
    window.location.href = '/checkout';
  }
}

/** カートに追加してトーストを表示する。画面遷移はしない */
class ProductSetAddToCart extends ProductSetCartAction {
  request(variantId) {
    const body = { id: variantId, quantity: 1 };
    if (cartIconBubble()) {
      body.sections = 'cart-icon-bubble';
      body.sections_url = window.location.pathname;
    }
    return postJSON(routeFor('cart_add_url', '/cart/add.js'), body);
  }

  onSuccess(data) {
    updateCartIconBubble(data);
    // 点数取得を待たずにトーストを出し、届き次第「カートを見る」の件数を差し替える
    showToast(addedToCartMessage(), { variant: 'success', cartLink: true });
    refreshCartCount();
  }
}

/**
 * トーストを他スクリプトからも使えるよう公開する。
 * Dawn 由来の product-form.js は <cart-notification> を前提にしているが、
 * このテーマでは同要素を出していないためこちらにフォールバックさせる。
 */
window.productSetToast = {
  show: showToast,
  hide: hideToast,
  addedMessage: addedToCartMessage,
  errorMessage: defaultErrorMessage,
  refreshCount: refreshCartCount,
};

if (!customElements.get('product-set-buy-now')) {
  customElements.define('product-set-buy-now', ProductSetBuyNow);
}
if (!customElements.get('product-set-add-to-cart')) {
  customElements.define('product-set-add-to-cart', ProductSetAddToCart);
}
